import { useAuth } from '@ocula/auth';
import { useWindowSize } from '@react-hookz/web/esm';
import { breakpoints } from '@ui/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';

import { DesktopNavigation, MobileNavigation } from '@src/components/Navigation';
import { FeatureFlagSettings } from '@src/components/Navigation/FeatureFlagSettings';
import { useCreateOrgUrl } from '@src/hooks/useCreateOrgUrl';
import { useFeatureFlags } from '@src/hooks/useFeatureFlags';
import type { AppPage } from '@src/types';

import { getNavigationSections } from './getNavigationSections';

export interface AuthenticatedLayoutProps {
  appPages: readonly AppPage[];
}

// TODO could add `min-height: 100dvh` for Safari once iOS 15.4 is released.
const GridLayout = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto minmax(10px, 1fr);
`;

const navBreakpoint = breakpoints.tablet;

export function AuthenticatedLayout({ appPages }: AuthenticatedLayoutProps) {
  const { organisations, logout, user } = useAuth();
  const [showDesktopNav, setShowDesktopNav] = useState(window.innerWidth > navBreakpoint);
  const { width } = useWindowSize();
  const { values: featureFlags } = useFeatureFlags();
  const createOrgUrl = useCreateOrgUrl();
  const { orgName, region } = useParams();
  const [openSettings, setOpenSettings] = useState<boolean>(false);

  const homePath = createOrgUrl('');
  const username = user?.email ?? '';

  const authorisedSections = useMemo(
    () => getNavigationSections(organisations?.[0]?.routes, appPages, orgName ?? null, region ?? null, featureFlags),
    [organisations, appPages, orgName, region, featureFlags],
  );

  // Previous approach with useMediaQuery did not reliably fire on window resizes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    setShowDesktopNav(window.innerWidth > navBreakpoint);
  }, [width]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('auth0DisplayName', user?.['https://ocula.tech/actions/organizations']?.display_name);
      localStorage.setItem('auth0Name', user?.['https://ocula.tech/actions/organizations']?.name);
      localStorage.setItem('auth0OrgId', user?.org_id);
    }
    if (region) {
      localStorage.setItem('auth0Region', region ?? '');
    }
  }, [region, user]);

  const onLogout = useCallback(() => logout({ returnTo: globalThis.location.origin }), [logout]);

  if (showDesktopNav) {
    return (
      <GridLayout>
        <DesktopNavigation
          sections={authorisedSections}
          homePath={homePath}
          username={username}
          onLogout={onLogout}
          onSettings={() => setOpenSettings(true)}
        />
        <FeatureFlagSettings open={openSettings} onClose={() => setOpenSettings(false)} />
        <Outlet />
      </GridLayout>
    );
  }

  return (
    <GridLayout>
      <MobileNavigation
        sections={authorisedSections}
        homePath={homePath}
        onLogout={onLogout}
        onSettings={() => setOpenSettings(true)}
      />
      <FeatureFlagSettings open={openSettings} onClose={() => setOpenSettings(false)} />
      <Outlet />
    </GridLayout>
  );
}
